import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebaseConfig";
import Swal from 'sweetalert2';

import Navbar from "../../components/navbar/navbar";
import UserNav from "../../components/navbar/user-nav";
import FooterTop from "../../components/footer-top";
import Footer from "../../components/footer";
import Breadcrumb from '../../components/Breadcrumb';
import { useUser } from "../../context/UserContext";
import { ListingGridSkeleton } from '../../components/loading-states';

interface AsesorData {
  id: string;
  nombre: string;
  telefono: string;
  calificacion: number;
  propiedad_id?: string[];
  foto?: string;
}

type AsesorConConteo = AsesorData & {
  conteoAprobadas: number;
};

export default function Agents() {
  const { t } = useTranslation();
  const { user } = useUser();
  const [asesores, setAsesores] = useState<AsesorConConteo[]>([]);
  const [filteredAsesores, setFilteredAsesores] = useState<AsesorConConteo[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const itemsPerPage = 8;

  useEffect(() => {
    const obtenerAsesores = async () => {
      try {
        const asesoresSnap = await getDocs(collection(db, "asesor"));
        // Filtrar solo activos o sin estatus (por defecto activos)
        const asesoresBase = asesoresSnap.docs
          .filter(doc => {
            const data = doc.data();
            return !data.estatus || data.estatus === 'activo';
          })
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })) as AsesorData[];
  
        const propiedadesSnap = await getDocs(
          query(
            collection(db, "propiedades"),
            where("estatus_publicacion", "==", "aprobada")
          )
        );
        const propiedades = propiedadesSnap.docs.map((doc) => doc.data());
  
        const asesoresConConteo: AsesorConConteo[] = asesoresBase.map((asesor) => {
          // ✅ CORREGIDO: Buscar propiedades asignadas usando asesor_doc_ids (doc.id de Firestore)
          const totalAprobadas = propiedades.filter((p: any) => {
            // ✅ Verificar si el asesor está en el array asesor_doc_ids
            if (Array.isArray(p.asesor_doc_ids)) {
              // Buscar por doc.id de Firestore (asesor.id)
              return p.asesor_doc_ids.includes(asesor.id);
            }

            // ⚠️ Fallback para estructura legacy (por compatibilidad temporal)
            if (Array.isArray(p.asesor_id)) {
              const asesorData = asesor as any;
              return p.asesor_id.includes(asesor.id) ||
                     (asesorData.asesor_id && p.asesor_id.includes(asesorData.asesor_id));
            }

            return false;
          }).length;
          
          console.log(`Asesor ${asesor.nombre} (ID: ${asesor.id}): ${totalAprobadas} propiedades`);
          
          return {
            ...asesor,
            conteoAprobadas: totalAprobadas,
          };
        });
  
        asesoresConConteo.sort((a, b) => a.nombre.localeCompare(b.nombre));
        setAsesores(asesoresConConteo);
        setFilteredAsesores(asesoresConConteo);
      } catch (error) {
        console.error("Error al obtener asesores o propiedades:", error);
        setError('Error al cargar los agentes. Por favor, intenta de nuevo.');
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudieron cargar los agentes. Por favor, intenta de nuevo más tarde.',
          confirmButtonColor: '#3085d6'
        });
      } finally {
        setLoading(false);
      }
    };

    obtenerAsesores();
  }, []);

  // Filtro de búsqueda
  useEffect(() => {
    const filtered = asesores.filter(asesor =>
      asesor.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asesor.telefono?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredAsesores(filtered);
    setCurrentPage(1);
  }, [searchTerm, asesores]);

  // Paginación
  const totalPages = Math.ceil(filteredAsesores.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedAsesores = filteredAsesores.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  

  return (
    <>
      {/* 🎯 Navegación consciente del usuario */}
      {user ? (
        <UserNav
          nombreUsuario={user.nombre}
          nombreInmobiliaria={user.inmobiliaria_nombre}
        />
      ) : (
        <Navbar transparent={false} />
      )}

      <Breadcrumb
        items={[
          { label: 'Inicio', path: '/' },
          { label: 'Agentes', path: '/agents' }
        ]}
      />

      <section className="bg-primary position-relative">
        <div className="container py-5">
          <div className="row justify-content-center text-center">
            <div className="col-lg-8">
              <h2 className="ipt-title text-light mb-3">Agentes Certificados AMPI</h2>
              <p className="ipn-subtitle text-light-50 mb-0">
                Conoce a nuestros agentes afiliados profesionales certificados en la Riviera Nayarit.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="gray-simple">
        <div className="container">
          {/* Filtro de búsqueda */}
          <div className="row mb-4">
            <div className="col-lg-6 mx-auto">
              <div className="form-group">
                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    placeholder={t('agents.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <i className="fa fa-search position-absolute top-50 end-0 translate-middle-y me-3"></i>
                </div>
              </div>
            </div>
          </div>

          {/* Resultados */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center">
                <p className="text-muted mb-0">
                  {t('common.showing')} {paginatedAsesores.length} {t('common.of')} {filteredAsesores.length} {t('agents.title').toLowerCase()}
                </p>
                {totalPages > 1 && (
                  <small className="text-muted">
                    Página {currentPage} de {totalPages}
                  </small>
                )}
              </div>
            </div>
          </div>

          {loading ? (
            <ListingGridSkeleton color="primary" cardShape="circle" />
          ) : error ? (
            <div className="text-center py-5">
              <div className="alert alert-danger d-inline-block">
                <i className="fas fa-exclamation-circle me-2"></i>
                {error}
              </div>
              <div className="mt-3">
                <button
                  className="btn btn-primary"
                  onClick={() => window.location.reload()}
                >
                  <i className="fas fa-redo me-2"></i>
                  Reintentar
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="row justify-content-center g-4">
            {paginatedAsesores.map((item) => (
              <div
                className="col-xl-3 col-lg-4 col-md-6 col-sm-12"
                key={item.id}
              >
                <div className="agents-grid card-agents rounded-3 shadow d-flex flex-column" style={{ height: '100%' }}>
                  <div className="agents-grid-wrap">
                    <div className="fr-grid-thumb mx-auto text-center mt-5 mb-3">
                      <Link
                        to={`/agent-page/${item.id}`}
                        className="d-inline-flex p-1 circle border"
                      >
                        {item.foto ? (
                          <img
                            src={item.foto}
                            className="img-fluid circle"
                            width="130"
                            alt={item.nombre}
                          />
                        ) : (
                          <div className="bg-light circle d-flex align-items-center justify-content-center" style={{ width: '130px', height: '130px' }}>
                            <i className="fas fa-user text-muted fa-3x"></i>
                          </div>
                        )}
                      </Link>
                    </div>
                    <div className="fr-grid-deatil text-center" style={{ minHeight: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div className="fr-grid-deatil-flex">
                        <h5 className="fr-can-name mb-0">
                          <Link to={`/agent-page/${item.id}`}>
                            {item.nombre}
                          </Link>
                        </h5>
                      </div>
                    </div>
                  </div>

                  <div className="fr-grid-info d-flex align-items-center justify-content-between px-4 py-4 mt-auto">
                    <div className="fr-grid-sder">
                      <ul className="p-0">
                        <li>
                          <strong>{t('common.phone')}:</strong>
                          <span className="fw-medium text-primary ms-2">
                            {item.telefono || t('common.notAvailable')}
                          </span>
                        </li>
                        <li>
                          <div className="fr-can-rating">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <i
                                key={star}
                                className={`fas fa-star fs-xs ${
                                  star <= Math.round(item.calificacion)
                                    ? "text-warning"
                                    : "text-muted"
                                }`}
                                style={{ margin: "0 2px" }}
                              ></i>
                            ))}
                          </div>
                        </li>
                      </ul>
                    </div>
                    <div className="fr-grid-deatil-flex-right flex-shrink-0">
                      <div className="agent-email">
                        <a
                          href={`mailto:${(item as any).correo || 'info@ampirivieranayarit.com'}`}
                          className="square--40 rounded text-icon bg-light-email"
                          style={{ width: 38, height: 38, fontSize: 16 }}
                        >
                          <i className="fa-solid fa-envelope-circle-check"></i>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
              </div>

              {/* Paginación */}
          {totalPages > 1 && (
            <div className="row mt-5">
              <div className="col-lg-12">
                <nav aria-label="Page navigation">
                  <ul className="pagination justify-content-center flex-wrap">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        {t('common.previous')}
                      </button>
                    </li>
                    {/* Mostrar solo un rango de páginas para mejor UX horizontal */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(pageNum)}
                          >
                            {pageNum}
                          </button>
                        </li>
                      );
                    })}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        {t('common.next')}
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          )}

              {/* Estados vacíos */}
              {filteredAsesores.length === 0 && searchTerm && (
                <div className="col-12 text-center py-5">
                  <i className="fa-solid fa-search text-muted" style={{ fontSize: '48px' }}></i>
                  <h4 className="mt-3 text-muted">No se encontraron agentes</h4>
                  <p className="text-muted">No hay agentes que coincidan con "{searchTerm}"</p>
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => setSearchTerm('')}
                  >
                    Limpiar búsqueda
                  </button>
                </div>
              )}

              {asesores.length === 0 && !searchTerm && (
                <div className="col-12 text-center py-5">
                  <i className="fa-solid fa-user-tie text-muted" style={{ fontSize: '48px' }}></i>
                  <h4 className="mt-3 text-muted">No hay agentes registrados</h4>
                  <p className="text-muted">Aún no se han registrado agentes en el sistema</p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
